'use client'

import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import { useCapabilities } from '@/hooks/use-capabilities'
import { useUpgradeHint } from '@/hooks/use-upgrade-hint'
import { isAlgoAllowed as capsAllowAlgo } from '@/lib/billing/capabilities'
import { OrderFormSummaryTab } from '@/state/order'
import { AlgoType } from '@/types/generated/algo'

// Each order-type tab maps to the algo the backend gates on, so the picker can
// disable types the user's plan doesn't allow (e.g. FREE lacks Limit-Chase/RFQ).
export const ORDER_TAB_ALGO: Record<OrderFormSummaryTab, AlgoType> = {
  [OrderFormSummaryTab.Market]: AlgoType.ALGO_TYPE_MARKET,
  [OrderFormSummaryTab.Limit]: AlgoType.ALGO_TYPE_LIMIT,
  [OrderFormSummaryTab.TWAP]: AlgoType.ALGO_TYPE_TWAP,
  [OrderFormSummaryTab.LimitChase]: AlgoType.ALGO_TYPE_LIMIT_CHASE,
  [OrderFormSummaryTab.RFQ]: AlgoType.ALGO_TYPE_RFQ,
  [OrderFormSummaryTab.AdvancedTwap]: AlgoType.ALGO_TYPE_ADVANCED_TWAP,
  [OrderFormSummaryTab.SingleHedge]: AlgoType.ALGO_TYPE_SINGLE_HEDGE,
  [OrderFormSummaryTab.Scale]: AlgoType.ALGO_TYPE_SCALE,
}

/** The translated name of an order-type tab ("Limit", "TWAP", …). */
export function useOrderTabLabel(): (tab: OrderFormSummaryTab) => string {
  const t = useTranslations('orderForm')
  return useCallback(
    (tab: OrderFormSummaryTab) => {
      switch (tab) {
        case OrderFormSummaryTab.Limit:
          return t('limit')
        case OrderFormSummaryTab.Market:
          return t('market')
        case OrderFormSummaryTab.TWAP:
          return t('twap')
        case OrderFormSummaryTab.LimitChase:
          return t('limitChase')
        case OrderFormSummaryTab.RFQ:
          return t('rfq')
        case OrderFormSummaryTab.AdvancedTwap:
          return t('advancedTwap')
        case OrderFormSummaryTab.SingleHedge:
          return t('singleHedge')
        case OrderFormSummaryTab.Scale:
          return t('scale')
        default:
          return tab
      }
    },
    [t]
  )
}

export interface OrderTabLock {
  /** Order-type name for the "<feature> is available on <plan>" upsell. */
  feature: string
  /** Cheapest plan that allows it, when the catalog has loaded. */
  plan?: string
}

/**
 * The plan lock on an order-type tab, or null when the user's plan allows it. A locked tab can
 * still be the selected one (a prompt-drafted TWAP, a persisted selection, a plan downgrade): the
 * form keeps it so the user sees what they asked for, and the submit button stays locked instead.
 */
export function useOrderTabLock(tab: OrderFormSummaryTab): OrderTabLock | null {
  const { isAlgoAllowed } = useCapabilities()
  const upgradeHint = useUpgradeHint()
  const tabLabel = useOrderTabLabel()
  const algo = ORDER_TAB_ALGO[tab]
  if (isAlgoAllowed(algo)) return null
  return { feature: tabLabel(tab), plan: upgradeHint((caps) => capsAllowAlgo(caps, algo)) }
}
